import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, X, Copy, Check, Clock, ArrowRight } from 'lucide-react';
import { useCart } from '../../context/CartContext';

const API = process.env.REACT_APP_BACKEND_URL;
const SEEN_KEY = 'trxl_dealseen';
const CODE_KEY = 'trxl_dealcode';

// Auf diesen Pfaden NICHT anzeigen (Checkout/Account/Admin/Auth)
const BLOCKED = ['/checkout', '/admin', '/auth', '/login', '/mein-konto', '/passwort'];

function two(n) { return String(n).padStart(2, '0'); }

export default function ExitIntentPopup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, sessionId } = useCart();

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deal, setDeal] = useState(null); // { code, expires_at, minutes }
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [copied, setCopied] = useState(false);
  const armedRef = useRef(false);

  const blocked = BLOCKED.some(p => location.pathname.startsWith(p));

  // Gestaffelter Rabatt: ab 500 EUR Warenwert 10 EUR, sonst 5 EUR
  const cartTotal = cart?.total || 0;
  const dealValue = cartTotal >= 500 ? 10 : 5;
  const minOrder = dealValue === 10 ? 500 : 50;

  const trigger = useCallback(() => {
    if (armedRef.current) return;
    if (blocked) return;
    if (sessionStorage.getItem(SEEN_KEY)) return;
    if (localStorage.getItem(CODE_KEY)) return; // hat bereits einen Deal
    armedRef.current = true;
    sessionStorage.setItem(SEEN_KEY, '1');
    setOpen(true);
  }, [blocked]);

  // Desktop: Maus verlässt oben das Fenster (Exit-Intent)
  useEffect(() => {
    if (blocked) return;
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && (!e.relatedTarget && !e.toElement)) trigger();
    };
    document.addEventListener('mouseout', onMouseOut);
    // Mobile/Touch-Fallback: nach 45s Aufenthalt
    const isTouch = window.matchMedia('(hover: none)').matches;
    let t = null;
    if (isTouch) t = setTimeout(trigger, 45000);
    return () => {
      document.removeEventListener('mouseout', onMouseOut);
      if (t) clearTimeout(t);
    };
  }, [trigger, blocked]);

  // Countdown
  useEffect(() => {
    if (!deal?.expires_at) return;
    const tick = () => {
      const diff = Math.max(0, Math.floor((new Date(deal.expires_at).getTime() - Date.now()) / 1000));
      setSecondsLeft(diff);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deal]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const val = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(val)) {
      setError('Bitte gib eine gültige E-Mail-Adresse ein.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/cart/deal-lock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: val, session_id: sessionId || '', cart_value: cart?.total || 0 }),
      });
      const data = await res.json();
      if (res.ok) {
        setDeal(data);
        localStorage.setItem(CODE_KEY, data.code);
      } else {
        setError(data.detail || 'Etwas ist schiefgelaufen. Bitte versuche es erneut.');
      }
    } catch {
      setError('Netzwerkfehler. Bitte versuche es erneut.');
    }
    setLoading(false);
  };

  const copyCode = () => {
    if (!deal?.code) return;
    try { navigator.clipboard.writeText(deal.code); } catch { /* noop */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const redeem = () => {
    setOpen(false);
    const hasItems = (cart?.item_count || 0) > 0;
    navigate(hasItems ? '/checkout' : '/shop');
  };

  const close = () => setOpen(false);

  if (!open) return null;

  const expired = deal && secondsLeft <= 0;

  return (
    <div className="dealx-overlay" data-testid="exit-intent-popup" onMouseDown={close}>
      <div className="dealx-card" onMouseDown={(e) => e.stopPropagation()}>
        <button className="dealx-close" onClick={close} data-testid="deal-close-btn" aria-label="Schließen">
          <X size={20} />
        </button>

        {!deal ? (
          <>
            <div className="dealx-badge"><Lock size={14} /> EXKLUSIV · NUR JETZT</div>
            <h2 className="dealx-title">Warte! Sichere dir <span>{dealValue}&nbsp;€ Sofort-Rabatt</span></h2>
            <p className="dealx-sub">
              Trag deine E-Mail ein und wir schalten dir sofort einen <strong>{dealValue}&nbsp;€-Gutschein</strong>
              &nbsp;(ab {minOrder}&nbsp;€ Bestellwert) frei – gültig für die nächsten <strong>15&nbsp;Minuten</strong>.
            </p>
            <form onSubmit={submit} className="dealx-form">
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="deine@email.de"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="dealx-input"
                data-testid="deal-email-input"
                autoFocus
              />
              <button type="submit" className="dealx-btn" disabled={loading} data-testid="deal-submit-btn">
                {loading ? 'Wird gesichert…' : 'Gutschein sichern'} <ArrowRight size={16} />
              </button>
            </form>
            {error && <p className="dealx-error" data-testid="deal-error">{error}</p>}
            <p className="dealx-bnpl">
              Oder zahle bequem <strong>später mit PayPal</strong> – erst in 30&nbsp;Tagen oder in Raten.
            </p>
            <p className="dealx-legal">Kein Spam. Abmeldung jederzeit möglich.</p>
          </>
        ) : (
          <>
            <div className="dealx-badge dealx-badge--ok"><Check size={14} /> GESICHERT</div>
            <h2 className="dealx-title">Dein <span>{Math.round(deal.discount_value)}&nbsp;€-Gutschein</span> ist bereit!</h2>
            <p className="dealx-sub">Löse ihn an der Kasse ein – ab {Math.round(deal.min_order_value)}&nbsp;€ Bestellwert.</p>

            <div className="dealx-codebox" onClick={copyCode} data-testid="deal-code" title="Zum Kopieren klicken">
              <span className="dealx-code">{deal.code}</span>
              <button className="dealx-copy" data-testid="deal-copy-btn" aria-label="Code kopieren">
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>

            {!expired ? (
              <div className="dealx-timer" data-testid="deal-timer">
                <Clock size={15} /> Gültig noch&nbsp;
                <strong>{two(Math.floor(secondsLeft / 60))}:{two(secondsLeft % 60)}</strong>
              </div>
            ) : (
              <div className="dealx-timer dealx-timer--exp" data-testid="deal-timer-expired">
                <Clock size={15} /> Gutschein abgelaufen
              </div>
            )}

            <button className="dealx-btn dealx-btn--wide" onClick={redeem} data-testid="deal-redeem-btn">
              Jetzt einlösen & sparen <ArrowRight size={16} />
            </button>
            <p className="dealx-bnpl">
              Lieber später zahlen? An der Kasse geht’s auch <strong>mit PayPal in Raten</strong>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
